import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FileScan, Upload, History, AlertTriangle, Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useCompany } from '@/context/CompanyContext';
import { invoiceIntakeService } from '@/lib/invoiceIntakeService';

export function InvoiceIntakeDashboardPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { activeCompany } = useCompany();
  const [stats, setStats] = useState<{ total: number; pending: number; discrepancies: number } | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!activeCompany?.id) return;
    setLoading(true);
    invoiceIntakeService.getDashboardStats(activeCompany.id).then((result) => {
      if (result.data) {
        setStats({ total: result.data.totalInvoices, pending: result.data.pendingReview, discrepancies: result.data.openDiscrepancies });
      }
      setLoading(false);
    });
  }, [activeCompany?.id]);

  const quickActions = [
    {
      icon: <Upload className="w-8 h-8" />,
      title: t('invoiceIntake.dashboard.upload', 'העלאת חשבונית חדשה'),
      description: t('invoiceIntake.dashboard.uploadDesc', 'צלם או העלה חשבונית ספק לסריקה אוטומטית'),
      path: '/app/invoice-intake/upload',
      color: 'bg-teal-50 text-teal-700 border-teal-200',
      iconColor: 'text-teal-600',
    },
    {
      icon: <History className="w-8 h-8" />,
      title: t('invoiceIntake.dashboard.history', 'היסטוריית חשבוניות'),
      description: t('invoiceIntake.dashboard.historyDesc', 'צפה בכל החשבוניות שנקלטו במערכת'),
      path: '/app/invoice-intake/history',
      color: 'bg-blue-50 text-blue-700 border-blue-200',
      iconColor: 'text-blue-600',
    },
    {
      icon: <AlertTriangle className="w-8 h-8" />,
      title: t('invoiceIntake.dashboard.discrepancies', 'חוסרים ופערים'),
      description: t('invoiceIntake.dashboard.discrepanciesDesc', 'מעקב אחרי חוסרים, נזקים ובקשות זיכוי מספקים'),
      path: '/app/invoice-intake/discrepancies',
      color: 'bg-amber-50 text-amber-700 border-amber-200',
      iconColor: 'text-amber-600',
    },
  ];

  return (
    <div className="max-w-5xl mx-auto space-y-8">
      <div className="flex items-center gap-4">
        <div className="p-3 bg-teal-100 rounded-xl">
          <FileScan className="w-8 h-8 text-teal-700" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {t('invoiceIntake.title', 'קליטת חשבוניות ומלאי')}
          </h1>
          <p className="text-gray-500 mt-1">
            {t('invoiceIntake.subtitle', 'סרוק חשבוניות ספק, השווה מול המלאי, ועקוב אחרי חוסרים')}
          </p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <p className="text-sm text-gray-500 mb-1">{t('invoiceIntake.stats.total', 'סה"כ חשבוניות')}</p>
          {loading ? (
            <Loader2 className="w-5 h-5 animate-spin text-gray-300" />
          ) : (
            <p className="text-2xl font-bold text-gray-900">{stats?.total ?? 0}</p>
          )}
        </div>
        <div className="bg-white rounded-xl border border-amber-200 p-5">
          <p className="text-sm text-amber-600 mb-1">{t('invoiceIntake.stats.pending', 'ממתינות לאישור')}</p>
          {loading ? (
            <Loader2 className="w-5 h-5 animate-spin text-gray-300" />
          ) : (
            <p className="text-2xl font-bold text-amber-700">{stats?.pending ?? 0}</p>
          )}
        </div>
        <div className="bg-white rounded-xl border border-red-200 p-5">
          <p className="text-sm text-red-600 mb-1">{t('invoiceIntake.stats.discrepancies', 'חוסרים פתוחים')}</p>
          {loading ? (
            <Loader2 className="w-5 h-5 animate-spin text-gray-300" />
          ) : (
            <p className="text-2xl font-bold text-red-700">{stats?.discrepancies ?? 0}</p>
          )}
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {quickActions.map((action) => (
          <button
            key={action.path}
            onClick={() => navigate(action.path)}
            className={`
              p-6 rounded-xl border-2 text-right transition-all duration-200
              hover:shadow-lg hover:-translate-y-0.5
              ${action.color}
            `}
          >
            <div className={`mb-4 ${action.iconColor}`}>
              {action.icon}
            </div>
            <h3 className="text-lg font-semibold mb-2">{action.title}</h3>
            <p className="text-sm opacity-80">{action.description}</p>
          </button>
        ))}
      </div>
    </div>
  );
}
